import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ADMIN_DIRECT_LINKS,
  ADMIN_NAV_GROUPS,
  INSTALLER_DIRECT_LINKS,
  INSTALLER_NAV_GROUPS,
  isPathActive,
} from "./nav-config";

interface MobileNavProps {
  role: "admin" | "installer";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileNav({ role, open, onOpenChange }: MobileNavProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Valitse oikeat linkit roolin mukaan
  const directLinks =
    role === "admin" ? ADMIN_DIRECT_LINKS : INSTALLER_DIRECT_LINKS;
  const navGroups =
    role === "admin" ? ADMIN_NAV_GROUPS : INSTALLER_NAV_GROUPS;

  const handleLinkClick = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild className="md:hidden">
        <Button variant="ghost" size="icon">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Avaa valikko</span>
        </Button>
      </SheetTrigger>

      <SheetContent side="left" className="w-72 p-0">
        <SheetHeader className="border-b px-4 py-4">
          <SheetTitle className="flex items-center gap-2 text-left">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold text-sm">
              SF
            </div>
            StoneFlow Pro
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-73px)]">
          <nav className="flex flex-col gap-1 p-4">
            {/* Suorat linkit */}
            {directLinks.map((link) => {
              const isActive = isPathActive(currentPath, link.to);

              return (
                <Link key={link.to} to={link.to} onClick={handleLinkClick}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className="w-full justify-start gap-3"
                  >
                    <link.icon className="h-4 w-4" />
                    {link.label}
                  </Button>
                </Link>
              );
            })}

            {/* Ryhmitellyt linkit */}
            {navGroups.map((group) => (
              <div key={group.id} className="mt-2">
                <Separator className="mb-3" />

                {/* Ryhmän otsikko */}
                <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <group.icon className="h-3.5 w-3.5" />
                  <span>{group.label}</span>
                </div>

                {/* Ryhmän linkit */}
                {group.links.map((link) => {
                  const isActive = isPathActive(currentPath, link.to);

                  return (
                    <Link key={link.to} to={link.to} onClick={handleLinkClick}>
                      <Button
                        variant={isActive ? "secondary" : "ghost"}
                        className="w-full justify-start gap-3 pl-6"
                      >
                        <link.icon className="h-4 w-4" />
                        {link.label}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
